import React, { TextareaHTMLAttributes, forwardRef } from 'react';
import { cn } from '../../utils/cn';

export interface TextareaProps extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'size'> {
  label?: string;
  error?: string;
  hint?: string;
  size?: 'sm' | 'md' | 'lg';
  resize?: 'none' | 'vertical' | 'horizontal' | 'both';
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      label,
      error,
      hint,
      size = 'md',
      resize = 'vertical',
      className,
      disabled,
      required,
      rows = 4,
      ...props
    },
    ref
  ) => {
    const sizeClasses: Record<string, string> = {
      sm: 'py-1.5 px-3 text-xs',
      md: 'py-2.5 px-3.5 text-sm',
      lg: 'py-3 px-4 text-base',
    };

    const resizeClasses: Record<string, string> = {
      none: 'resize-none',
      vertical: 'resize-y',
      horizontal: 'resize-x',
      both: 'resize',
    };

    const hasError = !!error;

    return (
      <div className="w-full">
        {/* Label */}
        {label && (
          <label
            className={cn(
              'block text-sm font-medium mb-2',
              hasError ? 'text-error-500' : 'text-monday-text-muted',
              disabled && 'opacity-50 cursor-not-allowed'
            )}
          >
            {label}
            {required && <span className="form-required-dot"></span>}
          </label>
        )}

        {/* Textarea */}
        <textarea
          ref={ref}
          rows={rows}
          disabled={disabled}
          className={cn(
            'w-full rounded-xl border bg-white/95 shadow-xs',
            'text-monday-text transition-all duration-200',
            'focus:outline-none focus:shadow-[0_0_0_4px_rgba(180,35,58,0.16)]',
            'placeholder:text-monday-text-muted/60',
            'disabled:bg-monday-header disabled:cursor-not-allowed disabled:opacity-60',
            sizeClasses[size],
            resizeClasses[resize],
            hasError
              ? 'border-error-500 focus:border-error-500 focus:shadow-[0_0_0_4px_rgba(199,51,64,0.16)]'
              : 'border-monday-border focus:border-primary-500 hover:border-neutral-300',
            className
          )}
          {...props}
        />

        {/* Hint or Error */}
        {(hint || error) && (
          <p className={cn('mt-1 text-xs', hasError ? 'text-error-500' : 'text-monday-text-muted')}>
            {error || hint}
          </p>
        )}
      </div>
    );
  }
);

Textarea.displayName = 'Textarea';
